import type { ComponentProps } from 'react';

import * as ContextMenuPrimitive from '@radix-ui/react-context-menu';
import { cn } from '@repo/lib/cn';
import { CheckIcon, ChevronRightIcon } from 'lucide-react';

export function ContextMenu(contextMenuProps: ComponentProps<typeof ContextMenuPrimitive.Root>) {
  const { ...props } = contextMenuProps;
  return <ContextMenuPrimitive.Root data-slot="context-menu" {...props} />;
}

export function ContextMenuTrigger(contextMenuTriggerProps: ComponentProps<typeof ContextMenuPrimitive.Trigger>) {
  const { className, ...props } = contextMenuTriggerProps;
  return <ContextMenuPrimitive.Trigger data-slot="context-menu-trigger" className={cn('select-none', className)} {...props} />;
}

export function ContextMenuGroup(contextMenuGroupProps: ComponentProps<typeof ContextMenuPrimitive.Group>) {
  const { className, ...props } = contextMenuGroupProps;
  return <ContextMenuPrimitive.Group data-slot="context-menu-group" className={cn('overflow-hidden p-1', className)} {...props} />;
}

export function ContextMenuPortal(contextMenuPortalProps: ComponentProps<typeof ContextMenuPrimitive.Portal>) {
  const { ...props } = contextMenuPortalProps;
  return <ContextMenuPrimitive.Portal data-slot="context-menu-portal" {...props} />;
}

export function ContextMenuSub(contextMenuSubProps: ComponentProps<typeof ContextMenuPrimitive.Sub>) {
  const { ...props } = contextMenuSubProps;
  return <ContextMenuPrimitive.Sub data-slot="context-menu-sub" {...props} />;
}

export function ContextMenuRadioGroup(contextMenuRadioGroupProps: ComponentProps<typeof ContextMenuPrimitive.RadioGroup>) {
  const { className, ...props } = contextMenuRadioGroupProps;
  return <ContextMenuPrimitive.RadioGroup data-slot="context-menu-radio-group" className={cn('overflow-hidden', className)} {...props} />;
}

export function ContextMenuSubTrigger(
  contextMenuSubTriggerProps: ComponentProps<typeof ContextMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
) {
  const { className, inset, children, ...props } = contextMenuSubTriggerProps;

  return (
    <ContextMenuPrimitive.SubTrigger
      data-slot="context-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "focus:bg-accent/80 focus:text-accent-foreground data-open:bg-accent/80 data-open:text-accent-foreground gap-1.5 rounded-md px-1.5 py-1 text-sm data-inset:pl-7 [&_svg:not([class*='size-'])]:size-3.5 flex cursor-pointer items-center outline-hidden select-none [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto" />
    </ContextMenuPrimitive.SubTrigger>
  );
}

export function ContextMenuSubContent(contextMenuSubContentProps: ComponentProps<typeof ContextMenuPrimitive.SubContent>) {
  const { className, ...props } = contextMenuSubContentProps;
  return (
    <ContextMenuPrimitive.SubContent
      data-slot="context-menu-sub-content"
      className={cn(
        'data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 bg-popover text-accent-foreground min-w-32 rounded-md border shadow-lg duration-150 z-50 origin-(--radix-context-menu-content-transform-origin) overflow-hidden',
        className
      )}
      {...props}
    />
  );
}

export function ContextMenuContent(contextMenuContentProps: ComponentProps<typeof ContextMenuPrimitive.Content>) {
  const { className, ...props } = contextMenuContentProps;
  return (
    <ContextMenuPrimitive.Portal>
      <ContextMenuPrimitive.Content
        data-slot="context-menu-content"
        className={cn(
          'data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 border bg-popover text-accent-foreground min-w-36 rounded-sm shadow-md duration-150 z-50 max-h-(--radix-context-menu-content-available-height) origin-(--radix-context-menu-content-transform-origin) overflow-x-hidden overflow-y-auto',
          className
        )}
        {...props}
      />
    </ContextMenuPrimitive.Portal>
  );
}

export function ContextMenuItem(
  contextMenuItemProps: ComponentProps<typeof ContextMenuPrimitive.Item> & {
    inset?: boolean;
    variant?: 'default' | 'destructive';
  }
) {
  const { className, inset, variant = 'default', ...props } = contextMenuItemProps;

  return (
    <ContextMenuPrimitive.Item
      data-slot="context-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "focus:bg-accent/80 focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:text-destructive focus:*:[svg]:text-accent-foreground gap-1.5 rounded-sm px-1.5 py-1 text-sm data-inset:pl-7 [&_svg:not([class*='size-'])]:size-3.5 group/context-menu-item relative flex cursor-pointer items-center outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    />
  );
}

export function ContextMenuCheckboxItem(contextMenuCheckboxItemProps: ComponentProps<typeof ContextMenuPrimitive.CheckboxItem> & { inset?: boolean }) {
  const { className, children, checked, inset, ...props } = contextMenuCheckboxItemProps;
  return (
    <ContextMenuPrimitive.CheckboxItem
      data-slot="context-menu-checkbox-item"
      data-inset={inset}
      className={cn(
        "focus:bg-accent/80 focus:text-accent-foreground gap-1.5 rounded-sm py-1 pr-8 pl-1.5 text-sm data-inset:pl-7 [&_svg:not([class*='size-'])]:size-3.5 relative flex cursor-pointer items-center outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      checked={checked}
      {...props}
    >
      <span className="pointer-events-none absolute right-2">
        <ContextMenuPrimitive.ItemIndicator>
          <CheckIcon />
        </ContextMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </ContextMenuPrimitive.CheckboxItem>
  );
}

export function ContextMenuRadioItem(contextMenuRadioItemProps: ComponentProps<typeof ContextMenuPrimitive.RadioItem> & { inset?: boolean }) {
  const { className, children, inset, ...props } = contextMenuRadioItemProps;
  return (
    <ContextMenuPrimitive.RadioItem
      data-slot="context-menu-radio-item"
      data-inset={inset}
      className={cn(
        "focus:bg-accent/80 focus:text-accent-foreground gap-1.5 rounded-sm py-1 pr-8 pl-1.5 text-sm data-inset:pl-7 [&_svg:not([class*='size-'])]:size-3.5 relative flex cursor-pointer items-center outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      <span className="pointer-events-none absolute right-2">
        <ContextMenuPrimitive.ItemIndicator>
          <CheckIcon />
        </ContextMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </ContextMenuPrimitive.RadioItem>
  );
}

export function ContextMenuLabel(
  contextMenuLabelProps: ComponentProps<typeof ContextMenuPrimitive.Label> & {
    inset?: boolean;
  }
) {
  const { className, inset, ...props } = contextMenuLabelProps;
  return <ContextMenuPrimitive.Label data-slot="context-menu-label" data-inset={inset} className={cn('text-muted-foreground px-1.5 py-1 text-sm font-medium data-inset:pl-7', className)} {...props} />;
}

export function ContextMenuSeparator(contextMenuSeparatorProps: ComponentProps<typeof ContextMenuPrimitive.Separator>) {
  const { className, ...props } = contextMenuSeparatorProps;
  return <ContextMenuPrimitive.Separator data-slot="context-menu-separator" className={cn('bg-border-primary h-px', className)} {...props} />;
}

export function ContextMenuShortcut(contextMenuShortcutProps: ComponentProps<'span'>) {
  const { className, ...props } = contextMenuShortcutProps;

  return <span data-slot="context-menu-shortcut" className={cn('text-muted-foreground group-focus/context-menu-item:text-accent-foreground ml-auto text-xs tracking-widest', className)} {...props} />;
}
